import { addDoc, collection, doc, getDocs, query, serverTimestamp, updateDoc, where, writeBatch } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { db } from '../../config/firebaseConfig';
import { PROJECT_COLUMNS, isTestProjectFlag } from './projectColumns';
import { formatProjectDoc } from './useProjectDirectory';

/**
 * "Tabloyu İndir": every project, freshly read, with the columns visible in
 * the table (Turkish headers, full values, as before). Test projects are
 * left out for everyone but the main admin. `messages`: the notifications in
 * the current language.
 */
export const downloadProjectsExcel = async ({ isMainAdmin, columnKeys, messages }) => {
  try {
    toast.info(messages.preparing);
    // orderBy kullanılmıyor: müşterisi girilmemiş projeler çıktıdan düşmesin
    const snapshot = await getDocs(collection(db, 'Projects'));
    let projects = snapshot.docs.map((docSnap) => formatProjectDoc(docSnap, docSnap.data()));
    if (!isMainAdmin) projects = projects.filter((project) => !isTestProjectFlag(project));

    const ExcelJS = (await import('exceljs')).default;
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Projects');
    worksheet.columns = columnKeys.map((key) => ({
      header: PROJECT_COLUMNS[key].label,
      key,
      width: key === 'titleShort' || key === 'titleLong' ? 40 : 20,
    }));

    projects.forEach((project) => {
      const rowData = {};
      columnKeys.forEach((key) => {
        rowData[key] = project[key] || '';
      });
      worksheet.addRow(rowData);
    });

    worksheet.getRow(1).eachCell((cell) => {
      cell.font = { bold: true, color: { argb: 'FFE5E7EB' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF111827' } };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'projects.xlsx';
    link.click();
    window.URL.revokeObjectURL(url);
    toast.success(messages.success);
  } catch (error) {
    console.error('Error downloading Excel file:', error);
    toast.error(messages.error);
  }
};

// Yeni proje / test projesi oluşunca görev atanan kişiler
const PROJECT_TASK_EMAILS = [
  'melisa.sener@habcarecro.com',
  'aygul.dag@habcarecro.com',
  'ozge.yikimlar@habcarecro.com',
];

/**
 * "Yeni Proje Ekle": projeyi kaydeder ve belirli kişilere "Yeni Proje"
 * görevi açar (eski davranış). Görev açılan kişi sayısını döndürür.
 */
export const createProject = async (formData) => {
  const projectRef = await addDoc(collection(db, 'Projects'), formData);
  const projectId = projectRef.id;

  const employeesSnapshot = await getDocs(query(collection(db, 'Employees'), where('eMail', 'in', PROJECT_TASK_EMAILS)));
  const taskPromises = employeesSnapshot.docs.map((empDoc) => {
    const empData = empDoc.data();
    const name = `${empData.eName || ''} ${empData.eSurname || ''}`.trim();
    return addDoc(collection(db, 'Tasks'), {
      title: `Yeni Proje: ${formData.titleShort}`,
      description: `${formData.customer} müşterisi için yeni proje oluşturuldu. Proje detaylarını inceleyiniz ve gerekli işlemleri başlatınız.`,
      assigneeId: empDoc.id,
      assigneeName: name,
      assignees: [{ id: empDoc.id, name, email: empData.eMail }],
      priority: 'medium',
      status: 'pending',
      category: 'Proje Yönetimi',
      projectId,
      projectName: formData.titleShort,
      customerName: formData.customer,
      createdAt: new Date(),
      updatedAt: new Date(),
      dueDate: formData.projectDueDate ? new Date(formData.projectDueDate) : null,
      createdBy: 'System',
      requestType: 'PROJECT_TASK',
    });
  });
  await Promise.all(taskPromises);
  return employeesSnapshot.docs.length;
};

/** "Proje Düzenle": formun bütün alanlarını projeye yazar. */
export const updateProject = (projectId, formData) => updateDoc(doc(db, 'Projects', projectId), formData);

/**
 * Müşteri kartındaki "Ödeme Periyodu" (müşteri seçilince ödeme sıklığına
 * yazılır); müşteri bulunamazsa undefined.
 */
export const fetchCustomerPaymentPeriod = async (customerTitle) => {
  const snapshot = await getDocs(query(collection(db, 'Customers'), where('Başlık', '==', customerTitle)));
  return snapshot.empty ? undefined : snapshot.docs[0].data()['Ödeme Periyodu'];
};

// Test projesi oluşunca görev atanan kişiler (yeni projeyle aynı)
const TEST_PROJECT_TASK_EMAILS = [
  'melisa.sener@habcarecro.com',
  'aygul.dag@habcarecro.com',
  'ozge.yikimlar@habcarecro.com',
];

/**
 * Ana admin: bir test projesi, varsayılan FTE ve saatlik tarifeleri ve
 * belirli kişilere görevler oluşturur (eski davranış). Görev atanan kişi
 * sayısını döndürür.
 */
export const createTestProject = async () => {
  const testProjectData = {
    customer: 'Test Müşteri',
    titleShort: `Test Projesi ${new Date().toLocaleDateString('tr-TR')}`,
    titleLong: 'Bu bir test projesidir - Admin tarafından oluşturuldu',
    protocolNumber: `TEST-${Date.now()}`,
    projectPhase: 'Aktif',
    projectDate: new Date().toISOString().split('T')[0],
    projectDueDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    projectDepartment: 'Test Departmanı',
    projectCentersType: 'Test',
    createdAt: serverTimestamp(),
    isTestProject: true,
  };

  const projectRef = await addDoc(collection(db, 'Projects'), testProjectData);
  const projectId = projectRef.id;

  const employeesSnapshot = await getDocs(query(collection(db, 'Employees'), where('eMail', 'in', TEST_PROJECT_TASK_EMAILS)));
  const taskPromises = employeesSnapshot.docs.map((empDoc) => {
    const empData = empDoc.data();
    const name = `${empData.eName || ''} ${empData.eSurname || ''}`.trim();
    return addDoc(collection(db, 'Tasks'), {
      title: `Yeni Test Projesi: ${testProjectData.titleShort}`,
      description: `${testProjectData.customer} müşterisi için yeni test projesi oluşturuldu. Proje detaylarını inceleyiniz ve gerekli işlemleri başlatınız.`,
      assigneeId: empDoc.id,
      assigneeName: name,
      assignees: [{ id: empDoc.id, name, email: empData.eMail }],
      priority: 'low', // Test projesi için düşük öncelik
      status: 'pending',
      category: 'Test Projesi',
      projectId,
      projectName: testProjectData.titleShort,
      customerName: testProjectData.customer,
      createdAt: new Date(),
      updatedAt: new Date(),
      dueDate: new Date(testProjectData.projectDueDate),
      createdBy: 'System',
      requestType: 'TEST_PROJECT_TASK',
    });
  });

  // Tarifeler tek seferde yazılır
  const batch = writeBatch(db);
  for (let i = 0; i <= 10; i += 1) {
    const fteValue = (i / 10).toFixed(1);
    batch.set(doc(collection(db, 'Tariffs')), {
      project: projectId,
      type: 'FTE',
      value: fteValue,
      price: 1000 * (i / 10),
      currency: 'TRY',
      createdAt: new Date(),
      isTestTariff: true,
    });
  }
  ['50', '100', '150'].forEach((value) => {
    batch.set(doc(collection(db, 'Tariffs')), {
      project: projectId,
      type: 'Hourly',
      value,
      price: parseInt(value, 10),
      currency: 'TRY',
      createdAt: new Date(),
      isTestTariff: true,
    });
  });

  await Promise.all([batch.commit(), ...taskPromises]);
  return employeesSnapshot.docs.length;
};
